import { type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { RepairEntry, RepairReport, RepairSource } from '@/features/types';
import { Button } from '@/ui/components/Button';
import { Icon } from '@/ui/components/Icon';

import styles from './RepairLog.module.css';

/**
 * WHAT THE TOOL CHANGED IN YOUR BYTES, ONE ROW AT A TIME — ADR-0101 §7.15, §14.
 *
 * ═══ WHY THIS EXISTS AT ALL ═══
 *
 * This product rewrites the user's document without asking, on paste and inside Format. It is
 * the single most invasive thing it does, and until now the only account of it was a banner
 * that vanished on the first keystroke: `App.tsx` pins the repair banner to the document
 * version the repair produced, precisely so a stale claim cannot linger, and ADR-0026 is right
 * about that. The cost was that the FACTS went with it. A user who typed one character and
 * then wondered what had happened to their single quotes had nowhere left to look.
 *
 * So the banner stays transient and the facts move here: the same repair, kept as long as it
 * is the last one, behind a tab. ADR-0101 §14 records the trade — "the banner is transient and
 * its facts live on in the Repair log".
 *
 * ═══ THE DIFF IS THE POINT, NOT DECORATION ═══
 *
 * A chip says "Use double quotes ×2"; a row says `'edge'` → `"edge"` at 7:25. Only the second
 * can be CHECKED. The before is struck through in coral (the family that means "bytes you
 * lost") and the after is lime ("valid"), which is the same pair the editor uses for an error
 * line and a repaired one — so the colours are read from one vocabulary rather than invented
 * for this list.
 *
 * A deletion has no after and an INSERTION has no before; both render an italic word in
 * `--tx-2` rather than an empty chip, because an empty `<ins>` or `<del>` is indistinguishable
 * from a rendering bug. The insertion half was missing until the review that added it, and it
 * is the half that shows most often — four of the rules a pasted document meets first insert
 * rather than replace.
 *
 * ═══ WHY `<del>` AND `<ins>` ═══
 *
 * They are the HTML for exactly this. A screen reader may announce them as "deletion" and
 * "insertion", which is a channel the strike-through and the colour cannot reach; and if the
 * stylesheet fails to load, `<del>` still renders struck. The alternative was two `<span>`s
 * carrying the meaning only in CSS, which is the mistake DESIGN §6.4's "shape AND word" rule
 * exists to prevent, one layer down.
 *
 * ═══ NOT PERSISTED ═══
 *
 * ADR-0023: nothing about a document leaves the tab, and that includes into `localStorage`.
 * `App.tsx` holds the last report in React state; a reload loses it, which is correct — a log
 * of repairs to a document that is no longer open describes nothing.
 */

export interface RepairLogProps {
  readonly report: RepairReport;
  /**
   * Reverses the WHOLE repair. Omitted ⇒ the button is not drawn, which is what the shell does
   * once the document has moved past the version the repair produced: `EditorHandle.undo()`
   * would then reverse whatever the user did last instead, and a button that undoes something
   * other than what it names is worse than no button.
   */
  readonly onUndoAll?: (() => void) | undefined;
  /** `'⌘Z'`, already rendered for this platform by the composition root. */
  readonly undoKeycap?: string | undefined;
  readonly className?: string;
  readonly id?: string;
}

/**
 * `RepairSource` → the words in the head. The enum travels from `features/**\/*.ts`, which does
 * not translate (ADR-0003); this is the one place it becomes English, and `i18n:check` proves
 * all three keys exist.
 */
const SOURCE_KEY = {
  paste: 'editor.repairLog.sourcePaste',
  format: 'editor.repairLog.sourceFormat',
  'fix-all': 'editor.repairLog.sourceFixAll',
} as const satisfies Record<RepairSource, string>;

export function RepairLog({
  report,
  onUndoAll,
  undoKeycap,
  className,
  id,
}: RepairLogProps): ReactElement {
  const { t } = useTranslation();

  const source = t(SOURCE_KEY[report.source]);

  return (
    <section
      aria-label={t('editor.repairLog.title')}
      className={[styles.log, className].filter(Boolean).join(' ')}
      id={id}
    >
      <div className={styles.head}>
        {/* The count is `entries.length` and not a number passed alongside it — the same
            invariant the repair banner keeps between its headline and its chips (DESIGN §6.5),
            here between the head and the rows. It cannot be off by one because there is
            nothing for it to be off by one FROM. */}
        <span className={styles.headText}>
          {t('editor.repairLog.head', { count: report.entries.length, source })}
        </span>
        {onUndoAll === undefined ? null : (
          <Button
            className={styles.undo}
            hue="lemon"
            icon="undo"
            label={t('action.undoAll')}
            onClick={onUndoAll}
            size="sm"
            variant="tinted"
            {...(undoKeycap === undefined ? {} : { shortcutHint: undoKeycap })}
          />
        )}
      </div>

      <ul className={styles.list} role="list">
        {report.entries.map((entry, index) => (
          // The index is a legitimate key here: the list is replaced wholesale whenever a new
          // repair happens, rows are never reordered, inserted into or removed from, and two
          // repairs by the same rule at the same position are genuinely indistinguishable.
          <li className={styles.item} key={`${String(index)}:${entry.label}`}>
            <Row
              addedLabel={t('editor.repairLog.added')}
              entry={entry}
              positionLabel={t('editor.problems.position', {
                line: entry.line,
                column: entry.column,
              })}
              removedLabel={t('editor.repairLog.removed')}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * ONE ROW IS A DIFF, AND A DIFF HAS TWO DEGENERATE SIDES.
 *
 * `core/repair/engine.ts` builds each entry's `before` as `text.slice(from, to)` over the
 * span the proposal replaces, and `after` as the replacement. A deletion therefore has an
 * empty `after` and an INSERTION has an empty `before` — and an insertion is the common case,
 * not the exotic one: insert-missing-comma, insert-missing-colon, close-open-containers and
 * close-unterminated-string are four of the rules a pasted document meets first.
 *
 * Only the deletion was handled. An insertion rendered `<del></del>`, which the stylesheet
 * draws as a 12×6px struck coral pill with nothing inside it — a chip that says "these bytes
 * were taken away" about no bytes at all. Both sides now name what happened in words, in the
 * same italic `.none` the deletion already used, so the row reads "insert the missing comma ·
 * added → `,`" instead of showing an empty wound.
 */
function Row({
  addedLabel,
  entry,
  positionLabel,
  removedLabel,
}: {
  readonly addedLabel: string;
  readonly entry: RepairEntry;
  readonly positionLabel: string;
  readonly removedLabel: string;
}): ReactNode {
  return (
    <>
      <span aria-hidden="true" className={styles.disc}>
        <Icon name="spark" size={15} />
      </span>
      <span className={styles.body}>
        <span className={styles.what}>{entry.label}</span>
        <span className={styles.diff}>
          {entry.before === '' ? (
            <em className={styles.none}>{addedLabel}</em>
          ) : (
            <del>{entry.before}</del>
          )}
          <span aria-hidden="true" className={styles.arrow}>
            →
          </span>
          {entry.after === '' ? (
            <em className={styles.none}>{removedLabel}</em>
          ) : (
            <ins>{entry.after}</ins>
          )}
          {/* `editor.problems.position`, the SAME template a problem row uses. Two spellings
              of "29:7" in one product would be one to keep in step for nothing, and a locale
              that writes positions differently should have to say so exactly once. */}
          <span className={styles.at}>{positionLabel}</span>
        </span>
      </span>
    </>
  );
}

export default RepairLog;
